import { VALIDATION_PATTERNS, VALIDATION_MESSAGES } from './constants';
import { validateDateRange } from './dateUtils';
import performanceTracker from './performanceTracker';

/**
 * Utilitaires de validation pour SessionForm - VERSION 100% UNIFIÉE
 */

/**
 * Reporter une erreur de validation au système de tracking
 */
const trackValidationError = (errors, context = {}) => {
  if (errors && errors.length > 0) {
    performanceTracker.trackError({
      type: 'validation_error',
      message: errors.join(', '),
      errors: errors,
      context: {
        ...context,
        url: window.location.pathname
      }
    });
  }
};

/**
 * Valider les champs obligatoires de base
 * @param {object} formData - Données du formulaire
 * @returns {object} Résultat de validation
 */
export const validateBasicFields = (formData) => {
  const errors = [];

  // Champs obligatoires communs - group_id (nouveau) ou room_id (ancien)
  if (!formData.group_id && !formData.room_id) {
    errors.push(VALIDATION_MESSAGES.ROOM_REQUIRED || 'Groupe de travail requis');
  }

  if (!formData.start_date) {
    errors.push('Date de début requise');
  }

  if (!formData.end_date) {
    errors.push('Date de fin requise');
  }

  // Validation des dates
  if (formData.start_date && formData.end_date) {
    const dateValidation = validateDateRange(formData.start_date, formData.end_date);
    if (!dateValidation.isValid) {
      errors.push(...dateValidation.errors);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * ✅ FONCTION UNIFIÉE : Compter le total de participants (TOUJOURS multi-jours)
 * @param {object} formData - Données du formulaire (fallback)
 * @param {object} multiDayData - Données multi-jours (source principale)
 * @returns {number} Nombre total de participants
 */
export const getTotalParticipantsCount = (formData, multiDayData = null) => {
  console.log('🔍 getTotalParticipantsCount appelée avec:', { formData, multiDayData });
  
  // ✅ PRIORITÉ 1 : Données multi-jours (source de vérité)
  if (multiDayData && multiDayData.dayParticipants) {
    console.log('📋 Mode unifié - Source: dayParticipants');
    const uniqueParticipants = new Set();
    
    Object.values(multiDayData.dayParticipants).forEach(dayParticipantList => {
      if (Array.isArray(dayParticipantList)) {
        dayParticipantList.forEach(participantId => {
          if (participantId) {
            uniqueParticipants.add(participantId);
          }
        });
      }
    });
    
    console.log('👥 Participants uniques (système unifié):', uniqueParticipants.size);
    console.log('📋 Liste:', Array.from(uniqueParticipants));
    return uniqueParticipants.size;
  }
  
  // ✅ PRIORITÉ 2 : Fallback sur formData si dayParticipants pas encore synchro
  console.log('📋 Mode unifié - Fallback: formData.participant_ids');
  const participantIds = formData?.participant_ids || [];
  console.log('👥 Participants fallback:', participantIds.length);
  console.log('📋 Liste:', participantIds);
  return participantIds.length;
};

/**
 * ✅ FONCTION UNIFIÉE : Valider les champs spécifiques aux prestations
 * @param {object} formData - Données du formulaire
 * @param {object} multiDayData - Données multi-jours
 * @returns {object} Résultat de validation
 */
export const validateTrainingFields = (formData, multiDayData = null) => {
  const errors = [];
  const sessionType = formData.prestation_type || formData.session_type;

  // Cette validation ne s'applique qu'au type 'prestation' (anciennement 'training')
  if (sessionType !== 'prestation' && sessionType !== 'training') {
    return { isValid: true, errors: [] };
  }

  // Chef d'équipe obligatoire pour les prestations
  if (!formData.team_leader_id && !formData.trainer_id) {
    errors.push(VALIDATION_MESSAGES.TRAINER_REQUIRED || 'Chef d\'équipe requis');
  }

  // ✅ Participants obligatoires uniquement pour les prestations
  const totalParticipants = getTotalParticipantsCount(formData, multiDayData);

  if (totalParticipants === 0) {
    errors.push('Au moins un participant est requis pour la prestation');
  }

  // Validation des heures (optionnel, pas bloquant)
  const trainingHours = parseFloat(formData.training_hours);
  if (formData.training_hours && (isNaN(trainingHours) || trainingHours < 0 || trainingHours > 24)) {
    errors.push('Heures de production invalides (0-24h)');
  }

  const travelHours = parseFloat(formData.travel_hours);
  if (formData.travel_hours && (isNaN(travelHours) || travelHours < 0 || travelHours > 24)) {
    errors.push('Heures de trajet invalides (0-24h)');
  }

  // Validation heure de départ si fournie (normaliser HH:MM:SS → HH:MM)
  if (formData.departure_time) {
    const normalizedTime = formData.departure_time.substring(0, 5); // Prendre uniquement HH:MM
    if (!VALIDATION_PATTERNS.TIME.test(normalizedTime)) {
      errors.push(VALIDATION_MESSAGES.INVALID_TIME);
    }
  }

  console.log('✅ Validation prestation terminée, erreurs:', errors);

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Valider les champs spécifiques aux blocages
 * @param {object} formData - Données du formulaire
 * @returns {object} Résultat de validation
 */
export const validateBlockedFields = (formData) => {
  const errors = [];
  const sessionType = formData.prestation_type || formData.session_type;

  if (sessionType !== 'blocked') {
    return { isValid: true, errors: [] };
  }

  if (!formData.block_reason || formData.block_reason.trim() === '') {
    errors.push(VALIDATION_MESSAGES.BLOCK_REASON_REQUIRED || 'Raison du blocage requise');
  }

  // Note: Les participants sont OPTIONNELS pour les blocages

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Valider les champs spécifiques à la maintenance
 * @param {object} formData - Données du formulaire
 * @returns {object} Résultat de validation
 */
export const validateMaintenanceFields = (formData) => {
  const errors = [];
  
  if (formData.session_type !== 'note' && formData.prestation_type !== 'note') {
    return { isValid: true, errors: [] };
  }
  
  if (!formData.maintenance_description || formData.maintenance_description.trim() === '') {
    errors.push(VALIDATION_MESSAGES.MAINTENANCE_DESCRIPTION_REQUIRED);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Valider l'email
 * @param {string} email - Email à valider
 * @returns {object} Résultat de validation
 */
export const validateEmail = (email) => {
  if (!email) {
    return { isValid: false, error: VALIDATION_MESSAGES.REQUIRED_FIELD };
  }
  
  if (!VALIDATION_PATTERNS.EMAIL.test(email)) {
    return { isValid: false, error: VALIDATION_MESSAGES.INVALID_EMAIL };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valider le numéro de téléphone
 * @param {string} phone - Téléphone à valider
 * @returns {object} Résultat de validation
 */
export const validatePhone = (phone) => {
  if (!phone) {
    return { isValid: true, error: null }; // Optionnel
  }
  
  if (!VALIDATION_PATTERNS.PHONE.test(phone)) {
    return { isValid: false, error: VALIDATION_MESSAGES.INVALID_PHONE };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valider l'heure au format HH:MM
 * @param {string} time - Heure à valider
 * @returns {object} Résultat de validation
 */
export const validateTime = (time) => {
  if (!time) {
    return { isValid: true, error: null }; // Optionnel
  }
  
  if (!VALIDATION_PATTERNS.TIME.test(time)) {
    return { isValid: false, error: VALIDATION_MESSAGES.INVALID_TIME };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valider les heures de formation/trajet
 * @param {number|string} hours - Heures à valider
 * @param {string} type - Type d'heures ('training' ou 'travel')
 * @returns {object} Résultat de validation
 */
export const validateHours = (hours, type = 'training') => {
  const numHours = parseFloat(hours);
  
  if (isNaN(numHours)) {
    return { 
      isValid: false, 
      error: `Heures de ${type === 'training' ? 'formation' : 'trajet'} invalides` 
    };
  }
  
  if (numHours < 0) {
    return { 
      isValid: false, 
      error: `Les heures ne peuvent pas être négatives` 
    };
  }
  
  if (numHours > 24) {
    return { 
      isValid: false, 
      error: `Les heures ne peuvent pas dépasser 24h` 
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * ✅ FONCTION UNIFIÉE : Valider les données multi-jours
 * @param {object} multiDayData - Données multi-jours
 * @param {string} sessionType - Type de session (prestation, blocked, note)
 * @returns {array} Liste des erreurs (array direct)
 */
export const validateMultiDayData = (multiDayData, sessionType = null) => {
  const errors = [];

  if (!multiDayData) {
    return errors; // Pas d'erreur si pas de données multi-jours
  }

  const { sessionDays = [], dayParticipants = {} } = multiDayData;

  // Validation seulement si vraiment multi-jours (plus d'un jour)
  if (sessionDays.length > 1) {
    if (sessionDays.length === 0) {
      errors.push('Au moins un jour doit être configuré');
    }

    // Vérifier les participants UNIQUEMENT pour les prestations
    // Les blocages et notes n'ont pas besoin de participants
    if (sessionType === 'prestation' || sessionType === 'training') {
      let totalDaysWithParticipants = 0;

      sessionDays.forEach(day => {
        const participants = dayParticipants[day.date] || [];
        if (participants.length > 0) {
          totalDaysWithParticipants++;
        }
      });

      if (totalDaysWithParticipants === 0) {
        errors.push('Au moins un jour doit avoir des participants');
      }
    }
  }

  return errors; // Toujours retourner un array
};

/**
 * ✅ VALIDATION DE CONFLITS (optionnelle)
 * @param {array} conflicts - Liste des conflits
 * @returns {object} Résultat de validation
 */
export const validateConflicts = (conflicts = []) => {
  const warnings = [];
  const errors = [];
  
  if (conflicts.length > 0) {
    warnings.push(`${conflicts.length} conflit(s) détecté(s) - vérifiez avant de sauvegarder`);
  }
  
  return {
    warnings,
    errors
  };
};

/**
 * ✅ FONCTION UNIFIÉE : Validation complète du formulaire
 * @param {object} formData - Données du formulaire
 * @param {object} options - Options de validation
 * @returns {object} Résultat de validation complète
 */
export const validateSessionForm = (formData, options = {}) => {
  console.log('🔍 === VALIDATION UNIFIÉE DU FORMULAIRE ===');
  console.log('📋 formData:', formData);
  console.log('📅 multiDayData:', options.multiDayData);

  const errors = [];
  const warnings = [];

  // Déterminer le type de session (nouveau ou ancien format)
  const sessionType = formData.prestation_type || formData.session_type;
  console.log('📋 Type de session:', sessionType);

  // 1. Validation des champs de base (groupe et dates)
  const baseValidation = validateBasicFields(formData);
  if (!baseValidation.isValid) {
    errors.push(...baseValidation.errors);
  }

  // 2. Validation selon le type de session
  if (sessionType === 'prestation' || sessionType === 'training') {
    // Prestation : chef d'équipe et participants obligatoires
    const trainingValidation = validateTrainingFields(formData, options.multiDayData);
    if (!trainingValidation.isValid) {
      errors.push(...trainingValidation.errors);
    }
  } else if (sessionType === 'blocked') {
    // Blocage : raison obligatoire, participants optionnels
    const blockedValidation = validateBlockedFields(formData);
    if (!blockedValidation.isValid) {
      errors.push(...blockedValidation.errors);
    }
  } else if (sessionType === 'note') {
    // Note : aucune validation spécifique, description optionnelle
    // Pas de validation supplémentaire
  }

  // 3. Validation des données multi-jours (passer le type pour savoir si participants requis)
  if (options.multiDayData) {
    const multiDayErrors = validateMultiDayData(options.multiDayData, sessionType);

    console.log('📊 Résultat validation multi-jours:', multiDayErrors);

    if (Array.isArray(multiDayErrors) && multiDayErrors.length > 0) {
      errors.push(...multiDayErrors);
    }
  }

  // 4. Validation des conflits (warnings seulement)
  if (options.conflicts && options.conflicts.length > 0) {
    const conflictValidation = validateConflicts(options.conflicts);
    if (conflictValidation.warnings && conflictValidation.warnings.length > 0) {
      warnings.push(...conflictValidation.warnings);
    }
  }

  console.log('✅ Validation unifiée terminée, erreurs totales:', errors.length);
  console.log('📋 Liste des erreurs:', errors);
  console.log('⚠️ Liste des warnings:', warnings);

  // Tracker les erreurs de validation pour le monitoring
  if (errors.length > 0) {
    trackValidationError(errors, {
      formType: 'session',
      sessionType: formData.prestation_type || formData.session_type,
      prestationId: formData.id
    });
  }

  return {
    isValid: errors.length === 0,
    canSaveWithWarnings: errors.length === 0 && warnings.length > 0,
    errors,
    warnings
  };
};

/**
 * ✅ FONCTION UNIFIÉE : Valider les données avant sauvegarde
 * @param {object} formData - Données du formulaire
 * @param {object} options - Options de validation
 * @returns {object} Résultat de validation
 */
export const validateBeforeSave = (formData, options = {}) => {
  console.log('🔍 === VALIDATION AVANT SAUVEGARDE ===');
  
  const { multiDayData, conflicts } = options;
  const errors = [];
  const warnings = [];
  
  // Validation complète du formulaire avec système unifié
  const formValidation = validateSessionForm(formData, { multiDayData, conflicts });
  
  if (!formValidation.isValid) {
    errors.push(...formValidation.errors);
  }
  
  if (formValidation.warnings && formValidation.warnings.length > 0) {
    warnings.push(...formValidation.warnings);
  }
  
  console.log('✅ Validation avant sauvegarde terminée');
  console.log('📊 Erreurs:', errors.length, 'Warnings:', warnings.length);
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    canSaveWithWarnings: errors.length === 0 && warnings.length > 0
  };
};

/**
 * Nettoyer et normaliser les données du formulaire
 * @param {object} formData - Données brutes du formulaire
 * @returns {object} Données nettoyées
 */
export const sanitizeFormData = (formData) => {
  const cleaned = { ...formData };
  
  // Nettoyer les chaînes de caractères
  ['block_reason', 'maintenance_description'].forEach(field => {
    if (cleaned[field] && typeof cleaned[field] === 'string') {
      cleaned[field] = cleaned[field].trim();
    }
  });
  
  // Normaliser les heures
  if (cleaned.training_hours) {
    cleaned.training_hours = parseFloat(cleaned.training_hours) || 7;
  }
  
  if (cleaned.travel_hours) {
    cleaned.travel_hours = parseFloat(cleaned.travel_hours) || 1;
  }
  
  // ✅ UNIFIÉ : Assurer que participant_ids est un tableau (pour compatibilité)
  if (!Array.isArray(cleaned.participant_ids)) {
    cleaned.participant_ids = [];
  }
  
  // Nettoyer les IDs vides
  cleaned.participant_ids = cleaned.participant_ids.filter(id => id && id !== '');
  
  return cleaned;
};

export default {
  validateBasicFields,
  validateTrainingFields,
  validateBlockedFields,
  validateMaintenanceFields,
  validateEmail,
  validatePhone,
  validateTime,
  validateHours,
  validateMultiDayData,
  validateSessionForm,
  validateBeforeSave,
  sanitizeFormData,
  getTotalParticipantsCount,
  validateConflicts // ✅ Nouvelle export
};